import React, { useEffect, useRef } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FiX, FiFileText, FiUser, FiCalendar, FiDatabase, FiBarChart, FiCheckCircle, FiAlertCircle, FiClock } from 'react-icons/fi';
import './FileDetailsModal.css';

const FileDetailsModal = ({ file, isOpen, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !file) return null;

  const getProgressData = (file) => {
    if (file.estado === 'PROCESANDO' && file.progreso_actual) {
      return {
        percentage: file.progreso_actual.percentage || 0,
        processed: file.progreso_actual.processed || 0,
        total: file.progreso_actual.total || 0,
        nuevos: file.progreso_actual.nuevos || 0,
        actualizados: file.progreso_actual.actualizados || 0,
        ignorados: file.progreso_actual.ignorados || 0
      };
    } else if (file.estado === 'COMPLETADO' && file.progreso) {
      return {
        percentage: file.progreso.percentage || 100,
        processed: file.progreso.processed || 0,
        total: file.progreso.total || 0,
        nuevos: file.progreso.nuevos || 0,
        actualizados: file.progreso.actualizados || 0,
        ignorados: file.progreso.ignorados || 0
      };
    }
    return null;
  };

  const getUserFromMetadata = (file) => {
    if (file.metadata?.resumen_carga?.usuario_carga && file.metadata.resumen_carga.usuario_carga !== 'None None') {
      return file.metadata.resumen_carga.usuario_carga;
    }
    return 'Sistema';
  };

  const formatFileType = (type) => {
    switch(type) {
      case 'CATASTRO_ENEL': return 'Catastro Enel';
      case 'CATASTRO_CIUDAD_LIMPIA': return 'Catastro Ciudad Limpia';
      case 'CATASTRO_ESTRUCTURA': return 'Catastro Estructura al Día';
      case 'RADICADOS_DIARIOS': return 'Radicados Diarios';
      case 'PLANO_ESCOMBROS': return 'Plano Escombros';
      default: return type;
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'COMPLETADO':
        return <FiCheckCircle className="status-icon completed" />;
      case 'PROCESANDO':
        return <FiClock className="status-icon processing" />;
      case 'ERROR':
        return <FiAlertCircle className="status-icon error" />;
      default:
        return <FiBarChart className="status-icon" />;
    }
  };

  const progressData = getProgressData(file);

  return (
    <div className="modal-wrapper">
      <Modal isOpen={isOpen} toggle={onClose} className="file-details-modal" size="lg" scrollable>
        <div className="modal-content-wrapper" ref={modalRef}>
          <div className="file-details-modal-header">
            <div className="file-details-modal-title">
              <FiFileText className="file-details-modal-icon" />
              <h2>Detalles del Archivo</h2>
            </div>
            <button className="file-details-modal-close-btn" onClick={onClose} type="button">
              <FiX />
            </button>
          </div>
      <ModalBody className="file-details-modal-body">
            <div className="file-details-grid">
          {/* Sección de información general */}
          <div className="file-details-general-info">
            <h3 className="file-details-section-title">
              <FiFileText className="section-title-icon" />
              Información General
            </h3>
            <div className="file-details-info-grid">
              <div className="file-details-info-item">
                <span className="file-details-info-label"><FiFileText /> Nombre del archivo:</span>
                <span className="file-details-info-value" title={file.archivo_nombre}>{file.archivo_nombre}</span>
              </div>
              
              <div className="file-details-info-item">
                <span className="file-details-info-label"><FiDatabase /> Tipo de archivo:</span>
                <span className="file-details-info-value">{formatFileType(file.tipo_archivo)}</span>
              </div>
              
              <div className="file-details-info-item">
                <span className="file-details-info-label"><FiBarChart /> Estado:</span>
                <span className={`file-details-status ${file.estado.toLowerCase()}`}>
                  {getStatusIcon(file.estado)}
                  {file.estado}
                </span>
              </div>
              
              <div className="file-details-info-item">
                <span className="file-details-info-label"><FiCalendar /> Fecha de carga:</span>
                <span className="file-details-info-value">{file.fecha_creacion}</span>
              </div>
              
              <div className="file-details-info-item">
                <span className="file-details-info-label"><FiUser /> Cargado por:</span>
                <span className="file-details-info-value">{getUserFromMetadata(file)}</span>
              </div>
              
              {progressData && (
                <div className="file-details-info-item">
                  <span className="file-details-info-label"><FiDatabase /> Total registros:</span>
                  <span className="file-details-info-value">{progressData.total}</span>
                </div>
              )}
              
              {file.observaciones && (
                <div className="file-details-info-item file-details-observaciones">
                  <span className="file-details-info-label"><FiFileText /> Observaciones:</span>
                  <span className="file-details-info-value" style={{ whiteSpace: 'pre-wrap' }}>{file.observaciones}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sección de progreso */}
          {progressData && (
            <div className="file-details-progress-section">
              <h3 className="file-details-section-title">
                <FiBarChart className="section-title-icon" />
                Progreso de Procesamiento
              </h3>
              
              <div className="file-details-progress-container">
                <div className="file-details-progress-bar">
                  <div 
                    className="file-details-progress-fill" 
                    style={{ width: `${progressData.percentage}%` }}
                  ></div>
                </div>
                <span className="file-details-progress-text">{progressData.percentage}% completado</span>
                
                <div className="file-details-progress-stats">
                  <div className="file-details-progress-stat">
                    <span className="file-details-progress-stat-label">Procesados:</span>
                    <span className="file-details-progress-stat-value">{progressData.processed}</span>
                  </div>
                  <div className="file-details-progress-stat">
                    <span className="file-details-progress-stat-label">Total:</span>
                    <span className="file-details-progress-stat-value">{progressData.total}</span>
                  </div>
                </div>
              </div>
              
              <div className="file-details-stats-grid">
                <div className="file-details-stat-card file-details-stat-new">
                  <div className="file-details-stat-number">{progressData.nuevos}</div>
                  <div className="file-details-stat-label">Nuevos registros</div>
                </div>
                <div className="file-details-stat-card file-details-stat-updated">
                  <div className="file-details-stat-number">{progressData.actualizados}</div>
                  <div className="file-details-stat-label">Actualizados</div>
                </div>
                <div className="file-details-stat-card file-details-stat-ignored">
                  <div className="file-details-stat-number">{progressData.ignorados}</div>
                  <div className="file-details-stat-label">Ignorados</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Metadata específica para Ciudad Limpia */}
        {file.tipo_archivo === 'CATASTRO_CIUDAD_LIMPIA' && file.metadata?.estadisticas_archivo && (
          <div className="file-details-metadata-section">
            <h3 className="file-details-section-title">
              <FiDatabase className="section-title-icon" />
              Estadísticas del Archivo - Ciudad Limpia
            </h3>
            
            {file.metadata.estadisticas_archivo.distribucion_barrios && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Barrios</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_barrios).map(([barrio, cantidad]) => (
                    <div key={barrio} className="file-details-chart-item">
                      <span className="file-details-chart-label">{barrio}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_barrios))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {file.metadata.estadisticas_archivo.distribucion_estratos && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Estratos</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_estratos).map(([estrato, cantidad]) => (
                    <div key={estrato} className="file-details-chart-item">
                      <span className="file-details-chart-label">Estrato {estrato}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_estratos))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {file.metadata.estadisticas_archivo.distribucion_tipos_productor && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Tipos de Productor</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_tipos_productor).map(([tipo, cantidad]) => (
                    <div key={tipo} className="file-details-chart-item">
                      <span className="file-details-chart-label">Tipo {tipo}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_tipos_productor))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metadata específica para Catastro Enel */}
        {file.tipo_archivo === 'CATASTRO_ENEL' && file.metadata?.estadisticas_archivo && (
          <div className="file-details-metadata-section">
            <h3 className="file-details-section-title">
              <FiDatabase className="section-title-icon" />
              Estadísticas del Archivo - Catastro Enel
            </h3>
            
            {file.metadata.estadisticas_archivo.distribucion_localidades && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Localidades</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_localidades).map(([localidad, cantidad]) => (
                    <div key={localidad} className="file-details-chart-item">
                      <span className="file-details-chart-label">{localidad}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_localidades))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {file.metadata.estadisticas_archivo.distribucion_estratos && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Estratos</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_estratos).map(([estrato, cantidad]) => (
                    <div key={estrato} className="file-details-chart-item">
                      <span className="file-details-chart-label">Estrato {estrato}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_estratos))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {file.metadata.estadisticas_archivo.distribucion_clases_servicio && (
              <div className="file-details-chart-section">
                <h4 className="file-details-chart-title">Distribución por Clases de Servicio</h4>
                <div className="file-details-chart-container">
                  {Object.entries(file.metadata.estadisticas_archivo.distribucion_clases_servicio).map(([clase, cantidad]) => (
                    <div key={clase} className="file-details-chart-item">
                      <span className="file-details-chart-label">{clase}</span>
                      <div className="file-details-chart-bar">
                        <div 
                          className="file-details-chart-fill" 
                          style={{ width: `${(cantidad / Math.max(...Object.values(file.metadata.estadisticas_archivo.distribucion_clases_servicio))) * 100}%` }}
                        ></div>
                      </div>
                      <span className="file-details-chart-value">{cantidad}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="file-details-additional-info">
              <div className="file-details-info-item">
                <span className="file-details-info-label">Registros con Marca:</span>
                <span className="file-details-info-value">{file.metadata.estadisticas_archivo.registros_con_marca || 0}</span>
              </div>
              <div className="file-details-info-item">
                <span className="file-details-info-label">Registros con Medidor:</span>
                <span className="file-details-info-value">{file.metadata.estadisticas_archivo.registros_con_medidor || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Detalles de procesamiento */}
        {file.metadata?.detalles_procesamiento && (
          <div className="file-details-processing-section">
            <h3 className="file-details-section-title">
              <FiDatabase className="section-title-icon" />
              Detalles de Procesamiento
            </h3>
            
            <div className="file-details-processing-grid">
              <div className="file-details-processing-item">
                <span className="file-details-processing-label">Registros Válidos:</span>
                <span className="file-details-processing-value">{file.metadata.detalles_procesamiento.registros_validos}</span>
              </div>
              <div className="file-details-processing-item">
                <span className="file-details-processing-label">Registros Esperados:</span>
                <span className="file-details-processing-value">{file.metadata.detalles_procesamiento.registros_esperados}</span>
              </div>
              <div className="file-details-processing-item">
                <span className="file-details-processing-label">Total Líneas Archivo:</span>
                <span className="file-details-processing-value">{file.metadata.detalles_procesamiento.total_lineas_archivo}</span>
              </div>
              <div className="file-details-processing-item">
                <span className="file-details-processing-label">Registros con Dirección:</span>
                <span className="file-details-processing-value">{file.metadata.detalles_procesamiento.registros_con_direccion}</span>
              </div>
              {file.metadata.detalles_procesamiento.registros_con_coordenadas && (
                <div className="file-details-processing-item">
                  <span className="file-details-processing-label">Registros con Coordenadas:</span>
                  <span className="file-details-processing-value">{file.metadata.detalles_procesamiento.registros_con_coordenadas}</span>
                </div>
              )}
            </div>
          </div>
        )}
            </ModalBody>
          </div>
        </Modal>
        <div className="modal-backdrop" onClick={onClose}></div>
      </div>
    );
};

export default FileDetailsModal;